import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';

/**
 * Custom subscription offer selector. Syncs selling_plan to the product form.
 *
 * @typedef {object} Refs
 * @property {HTMLFieldSetElement} purchaseTypes
 * @property {HTMLElement} subscriptionCard
 * @property {HTMLElement} onetimeCard
 * @property {HTMLInputElement} subscriptionRadio
 * @property {HTMLInputElement} onetimeRadio
 * @property {HTMLElement} subscriptionDetails
 * @property {HTMLSelectElement} planSelect
 * @property {HTMLElement} subscribePrice
 * @property {HTMLElement} [subscribePerServing]
 * @property {HTMLElement} [subscribeRecurringPrice]
 * @property {HTMLElement} [recurringPriceAmount]
 * @property {HTMLElement} [recurringPriceFrequency]
 *
 * @extends {Component<Refs>}
 */
class SubscriptionOffersComponent extends Component {
  /** @type {AbortController} */
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();
    const { signal } = this.#abortController;
    const closestSection = this.closest('.shopify-section, dialog');

    closestSection?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    closestSection?.addEventListener('click', this.#handleSectionClick, { capture: true, signal });
    closestSection?.addEventListener('submit', this.#handleFormSubmit, { capture: true, signal });

    this.#updateCardSelectionState();
    this.#updatePlanPricing();
    this.#scheduleFormSync();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    this.#abortController = new AbortController();
    this.#removeSellingPlanFromForm();
  }

  /**
   * Subscription offers render before buy-buttons in the DOM, so the product form
   * may not exist on first connect. Retry sync until the form is available.
   */
  #scheduleFormSync() {
    this.#syncSelectedPlanToForm();

    if (this.#getProductForm()) return;

    requestAnimationFrame(() => {
      this.#syncSelectedPlanToForm();

      if (this.#getProductForm()) return;

      requestAnimationFrame(() => {
        this.#syncSelectedPlanToForm();
      });
    });
  }

  /**
   * @param {Event} event
   */
  #handleSectionClick = (event) => {
    if (!(event.target instanceof Element)) return;

    if (event.target.closest('[ref="addToCartButton"], .sticky-add-to-cart__button, .shopify-payment-button')) {
      this.#syncSelectedPlanToForm();
    }
  };

  /**
   * @param {Event} event
   */
  #handleFormSubmit = (event) => {
    const form = this.#getProductForm();
    if (form && event.target === form) {
      this.#syncSelectedPlanToForm();
    }
  };

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = (event) => {
    const blockId = this.dataset.blockId;
    if (!blockId) return;

    const productId = this.dataset.productId;
    if (productId) {
      if (event.detail.data.newProduct && event.detail.data.newProduct.id !== productId) {
        return;
      }
      if (
        !event.detail.data.newProduct &&
        event.target instanceof HTMLElement &&
        event.target.dataset.productId !== productId
      ) {
        return;
      }
    }

    const next = event.detail.data.html.querySelector(
      `subscription-offers-component[data-block-id="${blockId}"]`
    );

    if (!next) {
      this.hidden = true;
      this.#removeSellingPlanFromForm();
      return;
    }

    this.hidden = false;

    if (next instanceof HTMLElement) {
      for (const key of ['productId', 'variantId', 'defaultPurchaseType', 'defaultPlanId', 'perServingSuffix', 'layout']) {
        const value = next.dataset[key];
        if (value) {
          this.dataset[key] = value;
        } else {
          delete this.dataset[key];
        }
      }
    }

    morph(this, next, { childrenOnly: true });
    this.#updateCardSelectionState();
    this.#updatePlanPricing();
    this.#scheduleFormSync();
  };

  handlePurchaseTypeChange = () => {
    this.#updateCardSelectionState();
    this.#syncSelectedPlanToForm();
  };

  /**
   * Fallback when label association does not cover the full card surface.
   * @param {Event} event
   */
  handlePurchaseTypeClick = (event) => {
    if (!(event.target instanceof Element)) return;
    if (event.target.closest('select, button, a, textarea, .subscription-offers__select')) return;

    const { subscriptionRadio, onetimeRadio } = this.refs;
    const onetimeOption = event.target.closest('.subscription-offers__option--onetime');
    const subscriptionOption = event.target.closest('.subscription-offers__option--subscription');

    if (onetimeOption && onetimeRadio && !onetimeRadio.checked) {
      onetimeRadio.checked = true;
      this.handlePurchaseTypeChange();
      return;
    }

    if (subscriptionOption && subscriptionRadio && !subscriptionRadio.checked) {
      subscriptionRadio.checked = true;
      this.handlePurchaseTypeChange();
    }
  };

  handlePlanChange = () => {
    this.#updatePlanPricing();
    this.#syncSelectedPlanToForm();
  };

  #updateCardSelectionState() {
    const { subscriptionCard, onetimeCard, subscriptionRadio, onetimeRadio, subscriptionDetails, planSelect } =
      this.refs;
    if (!subscriptionCard || !onetimeCard || !subscriptionRadio || !onetimeRadio) return;

    const isSubscription = subscriptionRadio.checked;
    subscriptionCard.classList.toggle('subscription-offers__option--selected', isSubscription);
    onetimeCard.classList.toggle('subscription-offers__option--selected', !isSubscription);

    if (subscriptionDetails) {
      subscriptionDetails.classList.toggle('subscription-offers__details--hidden', !isSubscription);
    }

    if (planSelect) {
      planSelect.disabled = !isSubscription;
    }
  }

  #updatePlanPricing() {
    const { planSelect, subscribePrice, subscribePerServing, subscribeRecurringPrice } = this.refs;
    if (!planSelect) return;

    const option = planSelect.selectedOptions[0];
    if (!option) return;

    if (this.dataset.layout === 'starter-kit' && subscribeRecurringPrice) {
      const frequency = option.dataset.frequencyLabel ?? '';
      const price = option.dataset.recurringPriceFormatted ?? option.dataset.priceFormatted ?? '';
      const { recurringPriceAmount, recurringPriceFrequency } = this.refs;

      if (recurringPriceAmount && price) {
        recurringPriceAmount.innerHTML = price;
      }

      if (recurringPriceFrequency && frequency) {
        recurringPriceFrequency.textContent = frequency;
      }

      return;
    }

    if (!subscribePrice) return;

    const priceFormatted = option.dataset.priceFormatted;
    if (priceFormatted) {
      subscribePrice.innerHTML = priceFormatted;
    }

    if (subscribePerServing) {
      const perServingFormatted = option.dataset.perServingFormatted;
      const suffix = this.dataset.perServingSuffix || 'per serving';

      if (perServingFormatted) {
        subscribePerServing.innerHTML = `${perServingFormatted} ${suffix}`;
        subscribePerServing.hidden = false;
      } else {
        subscribePerServing.hidden = true;
      }
    }
  }

  /**
   * @returns {HTMLFormElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const selector = `product-form-component[data-product-id="${productId}"] form`;
    const roots = [
      this.closest('.shopify-section'),
      this.closest('.product-details'),
      this.closest('dialog'),
    ].filter((root) => root instanceof HTMLElement);

    for (const root of roots) {
      const form = root.querySelector(selector);
      if (form instanceof HTMLFormElement) return form;
    }

    return null;
  }

  /**
   * @returns {string | null}
   */
  #getSelectedSellingPlanId() {
    const { subscriptionRadio, planSelect } = this.refs;
    if (!subscriptionRadio?.checked) return null;

    const selectedOption = planSelect?.selectedOptions[0];
    return selectedOption?.dataset.sellingPlanId ?? selectedOption?.value ?? this.dataset.defaultPlanId ?? null;
  }

  #syncSelectedPlanToForm() {
    const planId = this.#getSelectedSellingPlanId();
    const form = this.#getProductForm();
    if (!form) return;

    let input = form.querySelector('input[name="selling_plan"]');

    if (!planId) {
      input?.remove();
      return;
    }

    if (!(input instanceof HTMLInputElement)) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'selling_plan';
      form.appendChild(input);
    }

    input.value = planId;
  }

  #removeSellingPlanFromForm() {
    const form = this.#getProductForm();
    form?.querySelector('input[name="selling_plan"]')?.remove();
  }
}

if (!customElements.get('subscription-offers-component')) {
  customElements.define('subscription-offers-component', SubscriptionOffersComponent);
}
